import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  notification,
} from "antd";

import {
  ApiError,
  type CorrectionReasonRow,
  api,
} from "../api/client";
import { ManagedListTable } from "../components/settings/ManagedListTable";

interface FormValues {
  label: string;
}

/**
 * Settings page — manage correction-reason lookup rows.
 *
 * Uses the shared ``<ManagedListTable>`` for DnD reorder, archive/restore,
 * and inline edit. The page owns the create/edit modal because per-entity
 * fields differ; for correction-reasons it's just the ``label``.
 *
 * Backend endpoints touched:
 *   GET    /api/correction-reasons
 *   POST   /api/correction-reasons
 *   PATCH  /api/correction-reasons/{id}      (rename / restore)
 *   DELETE /api/correction-reasons/{id}      (soft-delete)
 *   PUT    /api/correction-reasons/reorder   (sort_order)
 */
export const CorrectionReasonsPage = () => {
  const [rows, setRows] = useState<CorrectionReasonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CorrectionReasonRow | null>(null);
  const [form] = Form.useForm<FormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // The list endpoint returns active + archived rows when no
      // ``active`` query param is supplied — the table needs both so
      // the "Archived (N)" section can render.
      const r = await api.listCorrectionReasons();
      setRows(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ label: "" });
    setModalOpen(true);
  };

  const openEdit = (row: CorrectionReasonRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({ label: row.label });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        if (values.label.trim() !== editing.label) {
          await api.updateCorrectionReason(editing.id, {
            label: values.label.trim(),
          });
        }
      } else {
        await api.createCorrectionReason(values.label.trim(), rows.length * 10);
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      notification.error({
        message: editing ? "Update failed" : "Create failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (row: CorrectionReasonRow) => {
    setBusy(true);
    try {
      await api.archiveCorrectionReason(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (row: CorrectionReasonRow) => {
    setBusy(true);
    try {
      await api.updateCorrectionReason(row.id, { active: true });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (orderedIds: number[]) => {
    // The table has already applied the optimistic order; we just push
    // the canonical sort_order values to the server.
    try {
      await api.reorderCorrectionReasons(orderedIds);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      // Refresh to restore the server's authoritative order; the
      // optimistic state in the table snaps back via its ``rows`` effect.
      await refresh();
    }
  };

  return (
    <>
      <ManagedListTable<CorrectionReasonRow>
        title="Correction reasons"
        description="Operator-editable list backing the correction reason dropdown. Archived rows preserve historical references on existing corrections."
        rows={rows}
        loading={loading}
        busy={busy}
        error={error}
        onErrorDismiss={() => setError(null)}
        addLabel="+ Add reason"
        labelOf={(r) => r.label}
        keyOf={(r) => r.id}
        isArchived={(r) => !r.active}
        // Correction reasons have no concept of "system" rows.
        isSystem={() => false}
        onReorder={onReorder}
        onCreate={openCreate}
        onEdit={openEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />
      <Modal
        title={editing ? "Edit correction reason" : "Add correction reason"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        confirmLoading={busy}
        destroyOnHidden
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: "Label is required" }]}
          >
            <Input maxLength={64} placeholder="e.g. Damaged bottle" autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
